// Plain-English reason for a failed folder load in the EMAIL sidebar.
//
// CONSUMER: internal-ui
//
// DECISION: the sidebar used to show one generic "Folders failed" row and
// keep the server's reason in a tooltip nobody hovers. Baker was stuck for
// days with no way to tell Microsoft being slow from Microsoft denying
// access (2026-11-02). The server already sends a stable `code`
// (workers/api/src/routes/email/folders.ts); this maps it to words and says
// whether a reconnect is the likely fix.

export interface EmailFolderFailure {
  /** One sentence, no jargon, safe to show on screen. */
  message: string;
  /** True when reconnecting Office 365 is the likely fix. */
  suggestReconnect: boolean;
}

export function describeEmailFolderFailure(
  code: string | null | undefined,
  status: number | null | undefined,
): EmailFolderFailure {
  switch (code) {
    case "graph_429":
      return {
        message: "Microsoft is asking us to slow down. Try again in a minute.",
        suggestReconnect: false,
      };
    case "graph_unavailable":
      return {
        message: "Microsoft is slow to answer for your mailbox. Try again in a minute.",
        suggestReconnect: false,
      };
    case "graph_403":
      return {
        message: "Microsoft denied access to your folders. Reconnecting Office 365 usually fixes it.",
        suggestReconnect: true,
      };
    case "mailbox_forbidden":
      return {
        message: "You don't have access to this mailbox. Switch back to My mailbox.",
        suggestReconnect: false,
      };
    case "integration_unconfigured":
      return {
        message: "Email isn't set up on our side yet. Tell Grant.",
        suggestReconnect: false,
      };
    case "graph_error":
      return {
        message: "Microsoft couldn't list your folders. Reconnecting Office 365 may help.",
        suggestReconnect: true,
      };
    default:
      return {
        message:
          status === 401
            ? "You were signed out. Refresh the page and sign in again."
            : "Couldn't load your folders. Try again in a moment.",
        suggestReconnect: false,
      };
  }
}
